import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { ConnectFeature, ProductAccessService } from 'src/app/services/product-access/product-access.service';

/**
 * Agents and teams can be restricted to specific roles in Admin → Tools
 * (2026-09-18). Routes declare the features that make them useful
 * (`data.features`, any-of): the hire wizard needs agents, the Marketplace
 * needs agents or teams. A denied role lands on the dashboard, which hides
 * the same affordances, instead of a dead end. Runs beside productAccessGuard
 * and re-attaches the same (idempotent) subscription so it never waits on a
 * verdict nobody asked for.
 */
export const connectFeatureGuard: CanActivateFn = async (route) => {
  const features = (route.data?.features as ConnectFeature[] | undefined) ?? [];
  if (features.length === 0) return true;

  const productAccess = inject(ProductAccessService);
  const organization = inject(OrganizationService);
  const firebase = inject(FirebaseService);
  const router = inject(Router);

  if (organization.loading) {
    try {
      await organization.isReady();
    } catch {
      return router.createUrlTree(['/login']);
    }
  }
  const orgId = organization.data?.id;
  const uid = firebase.auth.auth.currentUser?.uid;
  if (!orgId || !uid) return router.createUrlTree(['/login']);
  productAccess.subscribeTo(orgId, uid);

  const access = await productAccess.waitForFeatureAccess();
  return features.some((feature) => access[feature]) ? true : router.createUrlTree(['/agents']);
};
